import {
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  FullScreenLoading,
  Icon,
  LayerCatalogError,
  LayerLibraryTable,
  ScopeTabs,
  SELECTED_LAYERS_TABLE_WIDTH,
  SelectedLayersTable,
  Text,
} from '@civilgrid/ui/components';
import { useNavigate, useParams, useRouter } from '@tanstack/react-router';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useSnapshot } from 'valtio';
import { useAvailableLayers } from '~/hooks/useAvailableLayers';
import rootStore from '~/store';
import layerConfigStore, { canEditLayerConfig } from '~/store2/layerConfig';
import layerStore from '~/store2/layers';
import ProtectedRouteLayout from '../common/ProtectedRouteLayout';
import { DIGITIZED_SECTION_KEY } from './digitizedLibraryGroup';
import { resetDialogCopy, resolveLayerLibraryAccess, resolveLayerLibraryTab } from './helpers';
import {
  trackLayerLibraryClosed,
  trackLayerLibraryLayerAdded,
  trackLayerLibraryLayerRemoved,
  trackLayerLibraryOpened,
  trackLayerLibraryReset,
  trackLayerLibraryScopeChanged,
  trackLayerLibrarySearched,
} from './layerLibraryEvents';
import { useLayerLibraryCatalog } from './useLayerLibraryCatalog';

/**
 * Layer Library page. One page: the scope tabs switch which catalog slice
 * the Library table shows while the Selected Layers table is constant. Unverified users are
 * redirected home.
 *
 * Add/Remove mutate the shared layer-config store only (the document membership +
 * editable sets); the debounced document sync persists them separately. While the
 * document is loading or failed to load, the page is read-only — a failed load
 * surfaces a retry, never an editable default state.
 */
export default function LayerLibraryPage() {
  const selectedHeadingId = useId();
  const navigate = useNavigate();
  const router = useRouter();
  const { tabId } = useParams({ strict: false }) as { tabId?: string };
  const { currentOrganizationId, inUnverifiedMode } = useSnapshot(rootStore) as typeof rootStore;
  const access = resolveLayerLibraryAccess(inUnverifiedMode);
  const tab = resolveLayerLibraryTab(tabId);
  // Not fetched for a user about to be redirected.
  const catalogQuery = useAvailableLayers(access === 'allow' ? currentOrganizationId : undefined);
  const { loadState, libraryLayerIds, meta, catalogVersion, orgDefaultLeafIds } = useSnapshot(layerConfigStore);
  const { tilesConfig, customLayersTilesConfig } = useSnapshot(layerStore) as typeof layerStore;
  const canEdit = canEditLayerConfig(loadState);

  useEffect(() => {
    if (access === 'redirect') {
      navigate({ to: '/home', replace: true });
    }
  }, [access, navigate]);

  // Fire `layer_library_opened` once, when access first resolves to allowed.
  const openedTrackedRef = useRef(false);
  useEffect(() => {
    if (access === 'allow' && !openedTrackedRef.current) {
      openedTrackedRef.current = true;
      trackLayerLibraryOpened(currentOrganizationId);
    }
  }, [access, currentOrganizationId]);

  // Hand the catalog to the store once the query (re)resolves — the Library actions
  // (addToLibrary path hydration, ancestor pruning) join against it.
  useEffect(() => {
    if (catalogQuery.data) layerConfigStore.setCatalog(catalogQuery.data, currentOrganizationId ?? null);
  }, [catalogQuery.data, currentOrganizationId]);

  // Icon kinds/colors + the popover preview derive from the Map's session-cached tiles
  // config. Bootstrap fetches it on app start; the memoized fetch makes this a
  // no-op then, and a real (single) request only when the page is hit before bootstrap
  // resolves. Until it arrives, rows render the default icons.
  useEffect(() => {
    layerStore.fetchTilesConfig();
  }, []);
  const catalog = catalogQuery.data;
  const { constituentKeys, catalogRows, selectedRows, allTabLayersAdded, tabOptions, getLayerInfo } =
    useLayerLibraryCatalog({
      catalog,
      libraryLayerIds,
      tab,
      tilesConfig,
      customLayersTilesConfig,
    });
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [isOpeningReset, setIsOpeningReset] = useState(false);
  const [justAddedRow, setJustAddedRow] = useState<string | undefined>(void 0);

  // Reset Layers is disabled at defaults — the disabled state doubles as the "you're
  // at defaults" indicator. Snapshot fields are the reactive deps; the
  // proxy call reads the store's fresh values.
  // biome-ignore lint/correctness/useExhaustiveDependencies: snapshot fields stand in for the store reads inside isAtDefaultLayers
  const isAtDefaults = useMemo(
    () => layerConfigStore.isAtDefaultLayers(),
    [meta, libraryLayerIds, catalogVersion, orgDefaultLeafIds],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: orgDefaultLeafIds snapshot stands in for the store read inside hasOrgDefaults
  const resetCopy = useMemo(
    () => resetDialogCopy({ scope: 'user', hasOrgDefaults: layerConfigStore.hasOrgDefaults() }),
    [orgDefaultLeafIds, catalogVersion],
  );

  // Stable so the table's debounced search effect isn't reset on every render.
  const handleSearch = useCallback(() => {
    trackLayerLibrarySearched(currentOrganizationId);
  }, [currentOrganizationId]);

  if (access !== 'allow') return <FullScreenLoading />;

  const handleBack = () => {
    trackLayerLibraryClosed(currentOrganizationId);
    // Browser back when there's somewhere to go (the usual picker → Library flow);
    // fall back to the home map for a deep-linked Library page with no prior history.
    if (router.history.canGoBack()) {
      router.history.back();
    } else {
      navigate({ to: '/home' });
    }
  };

  const handleTabChange = (value: string) => {
    trackLayerLibraryScopeChanged(tab, value, currentOrganizationId);
    // Replace rather than push so switching scope tabs never grows the history
    // stack — Back then always returns to the page before the Library, not a
    // previously-viewed tab.
    navigate({ to: '/settings/layerLibrary/$tabId', params: { tabId: value }, replace: true });
  };

  // The synthetic Digitized Layers row fans out to its real constituent groups — the
  // store's ancestor hydration/pruning then handles the section's own document entry.
  const handleAddLayer = (rowId: string) => {
    if (!canEdit) return;
    const groupIds = rowId === DIGITIZED_SECTION_KEY ? constituentKeys : [rowId];
    for (const groupId of groupIds) layerConfigStore.addToLibrary(groupId);
    trackLayerLibraryLayerAdded(rowId, tab, currentOrganizationId);
    setJustAddedRow(rowId);
  };

  const handleRemoveLayer = (rowId: string) => {
    if (!canEdit) return;
    const groupIds = rowId === DIGITIZED_SECTION_KEY ? constituentKeys : [rowId];
    for (const groupId of groupIds) layerConfigStore.removeFromLibrary(groupId);
    trackLayerLibraryLayerRemoved(rowId, tab, currentOrganizationId);
  };

  const handleResetLayers = () => {
    if (!canEdit) return;
    const removedCount = selectedRows.length;
    const fallbackTarget = layerConfigStore.hasOrgDefaults() ? 'org' : 'civilgrid';
    layerConfigStore.resetToDefaultLayers();
    trackLayerLibraryReset(removedCount, currentOrganizationId, fallbackTarget);
    setIsResetDialogOpen(false);
  };

  const handleOpenResetDialog = async () => {
    // CG-725: refresh org defaults and WAIT before exposing confirm, so a mid-session change
    // to org defaults isn't applied against a stale set. loadOrgDefaults is current-org guarded
    // internally, so a response superseded by an org switch is ignored.
    setIsOpeningReset(true);
    try {
      if (currentOrganizationId) await layerConfigStore.loadOrgDefaults(currentOrganizationId);
    } finally {
      setIsOpeningReset(false);
    }
    setIsResetDialogOpen(true);
  };

  return (
    <ProtectedRouteLayout>
      <div data-testid="layer-library-page" className="flex h-full w-full grow flex-row">
        <aside className="w-64 shrink-0 border-r border-grey-30 p-3">
          <button
            type="button"
            onClick={handleBack}
            className="mb-3 flex items-center gap-1.5 border-transparent border-b text-blue-50 text-sm no-underline cursor-pointer hover:border-blue-50"
            data-testid="layer-library-back"
          >
            <Icon icon="arrowLeft" size={16} />
            <span>Back</span>
          </button>
          <div className="mb-3 text-base font-semibold">Layer Library</div>
          <ScopeTabs
            options={tabOptions}
            value={tab}
            onValueChange={handleTabChange}
            data-testid="layer-library-scope-tabs"
          />
        </aside>
        {/* min-w-0 is load-bearing: without it this flex item's min-width:auto inherits the
            catalog table's full column width, so wide/resized columns push the whole page
            (and the Selected panel) past the viewport instead of scrolling in place. */}
        <div className="flex h-full min-h-0 w-full min-w-0 flex-col p-3">
          {/* Settings-load status — read-only while loading, retry on failure. */}
          {loadState === 'loading' && (
            <div
              data-testid="layer-library-settings-loading"
              className="mb-3 shrink-0 rounded border border-grey-20 bg-grey-5 px-3 py-2 text-sm text-grey-60"
            >
              Loading your layer settings — changes are disabled until they arrive.
            </div>
          )}
          {loadState === 'failed' && (
            <div
              data-testid="layer-library-settings-failed"
              className="mb-3 flex shrink-0 items-center justify-between rounded border border-red-30 bg-red-5 px-3 py-2"
            >
              <Text className="text-sm text-grey-90">
                Couldn’t load your layer settings. You can browse the catalog, but changes are disabled.
              </Text>
              <Button
                variant="secondary"
                size="small"
                onClick={() => currentOrganizationId && layerConfigStore.bootstrap(currentOrganizationId)}
                data-testid="layer-library-settings-retry"
              >
                Retry
              </Button>
            </div>
          )}

          {/* Catalog + Selected side by side (the LayerConfig/Page storybook layout). */}
          <div className="flex min-h-0 w-full grow gap-3">
            {/* Tab-driven catalog table: civilgrid / special slices of the same rows. */}
            <section data-testid={`layer-library-catalog-${tab}`} className="min-h-0 min-w-0 flex-1">
              {catalogQuery.isError ? (
                <LayerCatalogError onRetry={() => catalogQuery.refetch()} testIdPrefix="layer-library-catalog" />
              ) : (
                <LayerLibraryTable
                  aria-label="Layer library"
                  rows={catalogRows}
                  isLoading={catalogQuery.isPending}
                  onAddLayer={canEdit ? (row) => handleAddLayer(row.id) : undefined}
                  onSearch={handleSearch}
                  getLayerInfo={getLayerInfo}
                  className="h-full min-h-0 min-w-0"
                  emptyState={
                    <Text className="text-sm text-grey-60" data-testid="layer-library-catalog-empty">
                      {allTabLayersAdded ? 'All Layers Added' : 'No layers match.'}
                    </Text>
                  }
                />
              )}
            </section>

            {/* Selected rail — the user's in-library set, constant across tabs. */}
            {/* Exact-fit fixed width: the panel is never squeezed, cut off, or horizontally
                scrolled — leftover space goes to the catalog table, which scrolls when tight. */}
            <section
              data-testid="layer-library-selected-layers"
              className="flex min-h-0 shrink-0 flex-col gap-2"
              style={{ width: SELECTED_LAYERS_TABLE_WIDTH }}
            >
              <div className="flex h-8 items-center justify-between">
                <div className="flex items-center gap-2">
                  <Text id={selectedHeadingId} className="text-base font-semibold text-primary">
                    Selected
                  </Text>
                  <Badge kind="count" size="small">
                    {selectedRows.length.toLocaleString()}
                  </Badge>
                </div>
                <Button
                  variant="danger"
                  size="small"
                  disabled={!canEdit || isAtDefaults || orgDefaultLeafIds === undefined || isOpeningReset}
                  onClick={handleOpenResetDialog}
                  data-testid="layer-library-reset-layers"
                >
                  <Icon icon="reset" size={14} /> Reset Layers
                </Button>
              </div>
              <SelectedLayersTable
                aria-labelledby={selectedHeadingId}
                rows={selectedRows}
                justAddedRowId={justAddedRow}
                isLoading={catalogQuery.isPending}
                onRemove={canEdit ? (row) => handleRemoveLayer(row.id) : undefined}
                getLayerInfo={getLayerInfo}
                className="min-h-0 flex-1"
                emptyState={
                  <Text className="text-sm text-grey-60" data-testid="layer-library-selected-empty">
                    No layers selected.
                  </Text>
                }
              />
            </section>
          </div>

          {/* Destructive-action confirmation for Reset Layers. */}
          <Dialog open={isResetDialogOpen} onOpenChange={setIsResetDialogOpen}>
            <DialogContent data-testid="layer-library-reset-dialog" title={resetCopy.title}>
              <DialogBody>
                <Text className="text-sm text-grey-90">{resetCopy.body}</Text>
              </DialogBody>
              <DialogFooter>
                <Button
                  variant="secondary"
                  onClick={() => setIsResetDialogOpen(false)}
                  data-testid="layer-library-reset-cancel"
                >
                  {resetCopy.cancel}
                </Button>
                <Button variant="danger" onClick={handleResetLayers} data-testid="layer-library-reset-confirm">
                  {resetCopy.confirm}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </ProtectedRouteLayout>
  );
}
